import { createTabRouter, type TabRouter } from "../app/TabRouter.js";
import type { DomainControllers } from "../app/createControllers.js";
import type { ProgressState } from "../app/saveSwap.js";
import type { FeatureFlags } from "../env/featureFlags.js";
import { t } from "../i18n/i18n.js";
import type { BattlefieldRunner } from "./BattlefieldRunner.js";
import { isTabLocked, tabUnlockChapter } from "../ui/tabLock.js";
import { shouldShowUnlockDot } from "../ui/tabUnlockDot.js";
import type { ShellFrame } from "../ui/ShellFrame.js";
import { TabRefresher } from "../ui/TabRefresher.js";
import { ToastPresenter } from "../ui/ToastPresenter.js";
import { ModalHost } from "../ui/ModalHost.js";
import { resolveTabTutorialStart } from "../tutorial/resolveTabTutorialStart.js";
import type { TutorialRunner } from "@yjsgames/phaser-tutorial";
import type { GuideQuestFlow } from "../app/GuideQuestFlow.js";
import { LEGACY_TAB_TUTORIAL_AUTO_START_ENABLED } from "../tutorial/tutorialPolicy.js";

const CG_DIAG = (import.meta.env["VITE_WEB_AD_PROVIDER"] as string | undefined) === "crazygames";

function diag(message: string, data?: Record<string, unknown>): void {
  if (!CG_DIAG) return;
  console.info(`[CG-DIAG][TabRouter] ${message}`, data ?? {});
}

export interface GameSceneTabRouterDeps {
  readonly features: FeatureFlags;
  readonly modals: ModalHost;
  readonly tabs: TabRefresher;
  readonly frame: () => ShellFrame;
  readonly runner: () => BattlefieldRunner | undefined;
  readonly controllers: () => DomainControllers;
  readonly progress: () => ProgressState;
  readonly lastEnteredTab: () => string | null;
  readonly setLastEnteredTab: (tab: string) => void;
  readonly tutorialRunner: () => TutorialRunner | undefined;
  readonly guideQuest: () => GuideQuestFlow | undefined;
  readonly gameviewExpanded: () => boolean;
  readonly toasts: ToastPresenter;
  readonly refreshTabRedDots: () => void;
  readonly persist: () => void;
  readonly refreshOnTabEnter: () => void;
  readonly evaluateCrazyGamesBanner: () => void;
  readonly legacyTabTutorialAutoStartEnabled?: boolean;
}

/** 탭 라우터 결선 — GameScene.create()에서 비대한 탭 전환 부수효과를 분리한다. */
export function createGameSceneTabRouter(deps: GameSceneTabRouterDeps): TabRouter {
  return createTabRouter(
    deps.features,
    {
      closeOverlays: () => deps.modals.closeOverlays(),
      applyVisibility: (vis) => deps.tabs.applyVisibility(vis),
      setBottomActive: (tab) => deps.frame().bottomTab.setActive(tab),
      onTabEntered: (tab) => {
        const controllers = deps.controllers();
        const progress = deps.progress();
        controllers.soundController.playUi("ui.tabOpen");

        let redDotDirty = false;
        if (deps.lastEnteredTab() === "codex" && tab !== "codex") {
          progress.codexSeenChapter = controllers.chapter.bestChapter;
          redDotDirty = true;
        }
        deps.setLastEnteredTab(tab);

        if (shouldShowUnlockDot(tab, controllers.chapter.bestChapter, progress.tabUnlockSeen)) {
          progress.tabUnlockSeen.push(tab);
          redDotDirty = true;
        }
        if (redDotDirty) {
          deps.refreshTabRedDots();
          deps.persist();
        }

        deps.tabs.resize(tab, deps.frame().tabContentRectFor(deps.gameviewExpanded()));
        if (tab === "battle") deps.runner()?.reconcileGameviewOffset();

        deps.guideQuest()?.notifyTabEntered(tab);

        const legacyTabTutorialAutoStart =
          deps.legacyTabTutorialAutoStartEnabled ?? LEGACY_TAB_TUTORIAL_AUTO_START_ENABLED;
        if (!legacyTabTutorialAutoStart) return;

        const runner = deps.tutorialRunner();
        if (!runner) return;
        if (controllers.guideQuest.active()) return;
        const id = resolveTabTutorialStart({
          tab,
          completed: progress.tutorialCompleted,
          hasScenario: (s) => runner.hasScenario(s),
          tutorialActive: runner.isActive,
        });
        if (id) runner.startScenario(id);
      },
      notifyAction: (tab) => deps.tutorialRunner()?.notifyAction("tab." + tab),
      onActivated: () => {
        diag("onActivated", {});
        deps.evaluateCrazyGamesBanner();
        deps.refreshOnTabEnter();
      },
      onTabLocked: (tab, req) => {
        deps.frame().bottomTab.shake(tab);
        deps.toasts.showToast(t("toast.tabLocked", { chapter: req }));
      },
    },
    (tab) => {
      const best = deps.controllers().chapter.bestChapter;
      return isTabLocked(tab, best) ? tabUnlockChapter(tab) : null;
    },
  );
}
